import { inject, injectable } from "inversify";
import { fromPromise, IPromiseBasedObservable } from "mobx-utils";
import { action, computed, makeObservable, observable, reaction, runInAction } from "mobx";
import { userManagementModals } from "./userManagementModals";
import { DisableUserModal } from "../modals/DisableUserModal";
import { arrayToCsv } from "@whatslly/commons/csvUtils";
import { getUserProviders } from "@whatslly/commons/userIdentityUtils";
import dayjs from "dayjs";
import { InviteUserModal } from "../modals/InviteUserModal";
import { UserManagementDataLayerSymbol } from "./UserManagementDataLayerSymbol";
import type { IUserManagementDataLayer, LoadUsersResponse } from "../types/IUserManagementDataLayer";
import type { UserManagementUser } from "../types/UserManagementUser";
import capitalize from "lodash/capitalize";
import debounce from "lodash/debounce";
import uniqBy from "lodash/uniqBy";
import { updateDayjsGlobalLocale } from "@whatslly/commons/utils";
import { downloadBlob } from "@whatslly/ui-components/componentsUtils";
import Toast from "@whatslly/ui-components/v2/Toaster/Toast";
import i18n from "../../i18n/config";
import type { TableSort } from "@whatslly/ui-components/v2/Tables/Table/TableTypes";
import { stripNumberStringToOnlyNumbers } from "@whatslly/commons/utils";
import { MixpanelService } from "@whatslly/commons/mixpanel/MixpanelService";
import type { SupportedSidecarChannel } from "@whatslly/commons/sidecarUtils";
import type { PermissionInstanceConfigs } from "@whatslly/commons/platformPermissions";
@injectable()
export class UserManagementStore {
  public downloadCsvResult: IPromiseBasedObservable<void> | undefined;
  public loadMoreResults: IPromiseBasedObservable<LoadUsersResponse | undefined> | undefined;
  private _hideInactiveUsers = true;
  private _filterGroups: string[] = [];
  private _filterPermissions: string[] = [];
  private _currentData?: LoadUsersResponse;
  private _sort: TableSort = { direction: "desc", keyInsideItem: "lastLogin" };
  private _onlineOnly = false;
  private _onlyShowRelatedToMe = false;
  constructor(
    @inject(UserManagementDataLayerSymbol)
    private userManagementDataLayer: IUserManagementDataLayer,
    @inject(MixpanelService) private mixpanelService: MixpanelService
  ) {
    makeObservable<
      UserManagementStore,
      | "_currentData"
      | "_hideInactiveUsers"
      | "_filterGroups"
      | "_filterPermissions"
      | "_sort"
      | "_onlineOnly"
      | "changeUserStatus"
      | "_onlyShowRelatedToMe"
    >(this, {
      _currentData: observable,
      _hideInactiveUsers: observable,
      _filterGroups: observable.ref,
      _filterPermissions: observable.ref,
      _sort: observable,
      _onlineOnly: observable,
      sort: computed,
      setSort: action,
      hideInactiveUsers: computed,
      filterGroups: computed,
      filterPermissions: computed,
      setFilters: action,
      onlineOnly: computed,
      searchTerm: computed,
      users: computed,
      changeUserStatus: action,
      downloadCsvResult: observable,
      loadMoreResults: observable,
      downloadCsvFile: action,
      setSearchTerm: action,
      updateDataAfterLoadMore: action,
      hasMoreResults: computed,
      groupNames: computed,
      setCurrentData: action,
      loadMore: action,
      resetData: action.bound,
      _onlyShowRelatedToMe: observable,
    });

    reaction(() => this.userManagementDataLayer.orgId.value, this.resetData);
    reaction(() => this.sort, this.resetData);
    reaction(
      () => this.userManagementDataLayer._searchTerm,
      debounce(() => this.resetData(), 800)
    );
  }

  get searchTerm() {
    return this.userManagementDataLayer._searchTerm;
  }
  setSearchTerm(newSearchTerm: string) {
    this.userManagementDataLayer.setSearchTerm(newSearchTerm);
  }

  resetData() {
    this.loadMoreResults = undefined;
    this.setCurrentData(undefined);
  }
  get hideInactiveUsers() {
    return this._hideInactiveUsers;
  }
  set hideInactiveUsers(newSearchTerm) {
    this.resetData();
    this._hideInactiveUsers = newSearchTerm;
  }

  get filterGroups() {
    return this._filterGroups;
  }
  set filterGroups(filterGroups) {
    this.resetData();
    this._filterGroups = filterGroups;
  }

  get filterPermissions() {
    return this._filterPermissions;
  }
  set filterPermissions(filterPermissions) {
    this.resetData();
    this._filterPermissions = filterPermissions;
  }

  setFilters({ filterGroups, filterPermissions }: { filterGroups: string[]; filterPermissions: string[] }) {
    this.resetData();
    this._filterGroups = filterGroups;
    this._filterPermissions = filterPermissions;
  }

  get onlineOnly() {
    return this._onlineOnly;
  }
  set onlineOnly(onlineOnly: boolean) {
    this.resetData();
    this._onlineOnly = onlineOnly;
  }

  setCurrentData(currentData: LoadUsersResponse | undefined) {
    this._currentData = currentData;
  }

  get sort() {
    return this._sort;
  }

  setSort(sort: TableSort) {
    this._sort = sort;
  }

  get onlyShowRelatedToMe() {
    return this.restrictToDirectReports === true ? true : this._onlyShowRelatedToMe;
  }

  set onlyShowRelatedToMe(onlyShowRelatedToMe: boolean) {
    this.resetData();
    this._onlyShowRelatedToMe = onlyShowRelatedToMe;
  }

  get restrictToDirectReports() {
    return Boolean(this.userManagementDataLayer.restrictToDirectReports);
  }

  async loadUsers(
    options: { hideInactiveUsers: boolean; filterGroups: string[]; filterPermissions: string[]; restrictToDirectReports?: boolean },
    nextLoadMoreRequestUrl: string | undefined | null,
    orgId: string
  ) {
    const { hideInactiveUsers, filterGroups, filterPermissions, restrictToDirectReports } = options;
    const { direction, keyInsideItem } = this.sort;

    return this.userManagementDataLayer.loadUsers(
      { hideInactiveUsers, filterGroups, filterPermissions, sort: direction, sortBy: keyInsideItem, restrictToDirectReports },
      nextLoadMoreRequestUrl,
      orgId
    );
  }

  get users() {
    const hideInactiveUsers = this.hideInactiveUsers;
    const filterGroups = this.filterGroups;
    const filterPermissions = this.filterPermissions;
    const onlineOnly = this.onlineOnly;
    if (this._currentData) {
      return fromPromise.resolve(this._currentData);
    }
    return fromPromise(
      this.userManagementDataLayer.orgId.then((orgId) => {
        return Promise.all([
          this.loadUsers({ hideInactiveUsers, filterGroups, filterPermissions, restrictToDirectReports: this.onlyShowRelatedToMe }, undefined, orgId),
          this.userManagementDataLayer.getUserLocale?.(),
        ]).then(([data, userLocale]) => {
          updateDayjsGlobalLocale(userLocale);
          const filtered = onlineOnly ? { ...data, users: data.users.filter((u) => u.isOnline) } : data;
          const enrich = this.userManagementDataLayer.enrichUsersWithDesktopVersionsPromise;
          const afterEnrich = enrich
            ? enrich.then(
                () => filtered,
                () => filtered
              )
            : Promise.resolve(filtered);
          return afterEnrich.then((result: LoadUsersResponse) => {
            this.setCurrentData(result);
            return result;
          });
        });
      })
    );
  }

  get hasMoreResults() {
    return Boolean(this._currentData?.hasMoreResults);
  }

  updateDataAfterLoadMore(
    newUsersList: UserManagementUser[],
    newNextLoadMoreRequestUrl: string | undefined | null,
    hasMoreResults: boolean | undefined | null
  ) {
    if (!this._currentData) {
      return;
    }
    this._currentData.users = newUsersList;
    this._currentData.newNextLoadMoreRequestUrl = newNextLoadMoreRequestUrl;
    this._currentData.hasMoreResults = hasMoreResults;
  }

  loadMore() {
    if (!this._currentData) {
      this.loadMoreResults = fromPromise(Promise.resolve(undefined));
      return;
    }
    const currentData = this._currentData;
    const hideInactiveUsers = this.hideInactiveUsers;
    const filterGroups = this.filterGroups;
    const filterPermissions = this.filterPermissions;
    const onlineOnly = this.onlineOnly;
    this.loadMoreResults = fromPromise(
      this.userManagementDataLayer.orgId.then((orgId) => {
        return this.loadUsers({ hideInactiveUsers, filterGroups, filterPermissions }, currentData.newNextLoadMoreRequestUrl, orgId).then((data) => {
          const enrich = this.userManagementDataLayer.enrichUsersWithDesktopVersionsPromise;
          const afterEnrich = enrich
            ? enrich.then(
                () => data,
                () => data
              )
            : Promise.resolve(data);
          return afterEnrich.then((enrichedData: LoadUsersResponse) => {
            const merged = uniqBy([...currentData.users, ...enrichedData.users], "uid");
            const finalList = onlineOnly ? merged.filter((u) => u.isOnline) : merged;
            this.updateDataAfterLoadMore(finalList, enrichedData.newNextLoadMoreRequestUrl, enrichedData.hasMoreResults);
            return currentData;
          });
        });
      })
    );
  }

  private async changeUserStatus(users: UserManagementUser[], newStatus: boolean) {
    runInAction(() => {
      users.forEach((user) => {
        user.tuvisUserStatus = newStatus;
      });
    });

    try {
      const orgId = await this.userManagementDataLayer.orgId;
      const res = await this.userManagementDataLayer.updateUserStatus(users, newStatus, orgId);
      if (!res.success) {
        runInAction(() => {
          users.forEach((user) => {
            user.tuvisUserStatus = !newStatus;
          });
        });
      }
    } catch {
      runInAction(() => {
        users.forEach((user) => {
          user.tuvisUserStatus = !newStatus;
        });
      });
    }
  }

  async onUserStatusChange(users: UserManagementUser[], newStatus: boolean) {
    if (newStatus) {
      await this.changeUserStatus(users, true);
      return;
    }
    const res = await userManagementModals.openModal(DisableUserModal).promise;
    if (res?.result) {
      await this.changeUserStatus(users, false);
    }
  }

  async downloadCsvFile() {
    try {
      this.downloadCsvResult = fromPromise(this.performCsvDownload());
    } catch {
      userManagementModals.openModal(Toast, {
        text: i18n.t("user_management.actions.download_csv_failure") || "Failed to download CSV",
        type: "error",
      });
    }
  }

  private async performCsvDownload(): Promise<void> {
    const orgId = await this.userManagementDataLayer.orgId;

    const queryParams = {
      hideInactiveUsers: this.hideInactiveUsers,
      filterGroups: this.filterGroups,
      filterPermissions: this.filterPermissions,
      sort: this.sort.direction,
      sortBy: this.sort.keyInsideItem,
    };

    const customFields = await this.fetchCustomFields();
    const groupNamesById = await this.fetchGroupNamesById();

    // Load all users with filters applied
    const { users, available_channels } = await this.userManagementDataLayer.loadAllUsers(orgId, queryParams);

    const filteredUsersForCsv = this.onlineOnly ? users.filter((u) => u.isOnline) : users;
    const csvData = this.createCsvData(filteredUsersForCsv, available_channels || {}, customFields, groupNamesById);

    const fileName = `users-${dayjs().format("YYYY-MM-DD-HH-mm-ss")}.csv`;
    downloadBlob(csvData, fileName, "text/csv;charset=utf-8;");
    this.mixpanelService.trackCsvExported({ tableName: "Users" });
  }

  private async fetchCustomFields(): Promise<string[]> {
    try {
      return (await this.userManagementDataLayer.orgConfigs?.then((orgConfigs) => orgConfigs?.configs?.custom_user_fields ?? [])) ?? [];
    } catch {
      return [];
    }
  }

  private async fetchGroupNamesById(): Promise<Record<string, string>> {
    try {
      const groups = await this.userManagementDataLayer.groupNames;
      return Object.fromEntries((groups ?? []).map((group) => [group._id, group.name]));
    } catch {
      return {};
    }
  }

  private createCsvData(
    users: UserManagementUser[],
    availableChannels: Record<string, boolean>,
    customFields: string[],
    groupNamesById: Record<string, string>
  ): string {
    // Extract active channels
    const channels = Object.entries(availableChannels)
      .filter(([, value]) => value)
      .map(([key]) => key);

    // Create headers
    const headers = this.createCsvHeaders(channels, customFields);

    // Create data rows
    const rows = users.map((user) => this.createUserDataRow(user, channels, customFields, users, groupNamesById));

    // Convert to CSV
    return arrayToCsv([headers, ...rows]);
  }

  private createCsvHeaders(channels: string[], customFields: string[]): string[] {
    return [
      "UserId",
      "Name",
      "Username",
      "Email",
      "Phone",
      "Profile",
      "Role",
      "Login provider",
      "Login Provider - User ID",
      "Groups",
      "Manager",
      "Created Date",
      "Last Login",
      "Last Login (Platform)",
      "Last Login (Desktop)",
      "Last Login (Extension)",
      "Last Login (Mobile)",
      "Status in Tuvis",
      "Desktop Version",
      "Terms of Use Accepted",
      ...(channels.length ? channels.map((key) => this.getChannelHeaderLabel(key)) : []),
      ...(customFields.length ? customFields.map(capitalize) : []),
    ];
  }

  private createUserDataRow(
    user: UserManagementUser,
    channels: string[],
    customFields: string[],
    allUsers: UserManagementUser[],
    groupNamesById: Record<string, string>
  ): string[] {
    // Process channel phones data
    const channelsData = this.processChannelData(user, channels);

    // Process custom fields
    const customFieldsData = customFields.map((field) => user?.customFields?.[field] ?? "");

    // Build the complete row
    return [
      user._id ?? "",
      user.name ?? "",
      user.username ?? "",
      user.email ?? "",
      user.phones ? user.phones.join(", ") : "",
      user.profileName ?? "",
      user.roleName ?? "",
      getUserProviders({ identities: user.identities }).join(", "),
      user.crmUserId ?? "",
      this.getUserGroupNames(user.groupIds, groupNamesById),
      this.getManagerName(user.managerId, allUsers),
      user.createdAt ?? "",
      user.lastLogin ?? "",
      user.lastPlatformLogin ?? "",
      user.lastDesktopLogin ?? "",
      user.lastExtensionLogin ?? "",
      user.lastMobileLogin ?? "",
      String(typeof user.tuvisUserStatus === "boolean" ? user.tuvisUserStatus : false),
      user.desktopVersion ?? "",
      user.termsOfUseAcceptedAt ?? "",
      ...channelsData,
      ...customFieldsData,
    ];
  }

  private processChannelData(user: UserManagementUser, channels: string[]): string[] {
    const formattedChannelPhones: Record<string, Record<string, string>> | undefined = user.formattedChannelPhones;

    if (!channels.length || !formattedChannelPhones) {
      return [];
    }

    return channels.map((key) => {
      const value = (user?.channels as any)?.[key];

      if (!value) return "";

      // Handle array of strings (waba, sidecar)
      if (Array.isArray(value) && typeof value[0] === "string") {
        return value
          .map((str) => stripNumberStringToOnlyNumbers(formattedChannelPhones?.[key]?.[str] ?? undefined))
          .filter(Boolean)
          .join(", ");
      }

      // Handle twilio array of objects
      if (Array.isArray(value) && typeof value[0] === "object" && "phoneSid" in value[0]) {
        return value
          .map((obj) => stripNumberStringToOnlyNumbers(formattedChannelPhones?.[key]?.[obj.phoneSid] ?? undefined))
          .filter(Boolean)
          .join(", ");
      }

      // Handle objects with enabled property (whatsapp-web, imessage, mobile-call-recording)
      if (typeof value === "object" && "enabled" in value && value.enabled) {
        return "enabled";
      }

      return "";
    });
  }

  private getChannelHeaderLabel(key: string): string {
    switch (key) {
      case "waba":
        return "WhatsApp Business API";
      case "twilio":
        return "Call recording";
      case "sidecar":
        return "Mobile";
      case "whatsapp-web":
        return "Desktop";
      case "imessage":
        return "iMessage";
      default:
        return capitalize(key);
    }
  }

  getManagerName(managerId: string | undefined, users: UserManagementUser[]) {
    return managerId ? (users.find((user) => user._id === managerId)?.name ?? "") : "";
  }

  private getUserGroupNames(groupIds: string[] | undefined, groupNamesById: Record<string, string>) {
    if (!Array.isArray(groupIds) || groupIds.length === 0) {
      return "";
    }

    // Drop ids that don't resolve to a group instead of falling back to the id itself: users carry
    // memberships of long-deleted groups, and a raw ObjectId in a customer-facing export is worse
    // than an empty cell. Every other consumer already ignores these ids. [EN-7086]
    return groupIds
      .map((groupId) => groupNamesById[groupId])
      .filter(Boolean)
      .join(", ");
  }

  async resendInvite(invite: UserManagementUser[]) {
    const result = await this.userManagementDataLayer.resendInvite(invite);
    userManagementModals.openModal(Toast, {
      text: result?.success ? i18n.t("user_management.actions.resend_success") : i18n.t("user_management.actions.resend_failure"),
      type: result?.success ? "regular" : "error",
    });
  }

  async inviteUsers(
    canCreateSidecar: boolean,
    availableChannelsForCreation: { label: string; value: SupportedSidecarChannel }[],
    instanceConfigs?: PermissionInstanceConfigs
  ) {
    const res = await userManagementModals.openModal(InviteUserModal, {
      canCreateSidecar,
      availableChannelsForCreation,
      instanceConfigs,
    }).promise;
    if (res && this._currentData) {
      await this.userManagementDataLayer.inviteUsers(res);
      this.resetData();
    }
  }

  getCrmLink(user: UserManagementUser) {
    return this.userManagementDataLayer.getUserCrmLink?.(user);
  }

  get groupNames() {
    return this.userManagementDataLayer.groupNames;
  }
}
